import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  completePosterUpload,
  createPosterUpload,
  createVideo,
  createVideos,
  deleteVideo,
  replaceVideo,
  retryVideo,
  updateVideo,
} from '../services/admin/admin-video.service';
import { uploadResumable } from '../services/gcs-resumable-upload';
import { PosterContentType } from '../common/model/dto/video/video-constants';
import {
  CreateVideoRequest,
  ReplaceVideoRequest,
  UpdateVideoRequest,
} from '../common/model/dto/video/video-requests';
import {
  ADMIN_VIDEOS_KEY,
  applyVideoUpdate,
  invalidateVideoQueries,
} from '../common/utils/video-query-cache';

export function useAdminVideoMutations() {
  const queryClient = useQueryClient();

  const create = useMutation({
    mutationFn: async (requests: CreateVideoRequest[]) =>
      requests.length === 1
        ? [await createVideo(requests[0])]
        : createVideos(requests),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [ADMIN_VIDEOS_KEY] }),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: UpdateVideoRequest;
    }) => updateVideo(id, request),
    onSuccess: (video) => {
      applyVideoUpdate(queryClient, video);

      return queryClient.invalidateQueries({ queryKey: [ADMIN_VIDEOS_KEY] });
    },
  });

  const replace = useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: string;
      request: ReplaceVideoRequest;
    }) => replaceVideo(id, request),
    onSettled: () => invalidateVideoQueries(queryClient),
  });

  const retry = useMutation({
    mutationFn: (id: string) => retryVideo(id),
    onSuccess: (video) => applyVideoUpdate(queryClient, video),
    onError: () => invalidateVideoQueries(queryClient),
  });

  const replacePoster = useMutation({
    mutationFn: async ({
      id,
      file,
      contentType,
    }: {
      id: string;
      file: File;
      contentType: PosterContentType;
    }) => {
      const { upload_url } = await createPosterUpload(id, {
        content_type: contentType,
        size_bytes: file.size,
      });
      await uploadResumable(upload_url, file, { contentType });

      return completePosterUpload(id);
    },
    onSuccess: (video) => applyVideoUpdate(queryClient, video),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteVideo(id),
    onSuccess: () => invalidateVideoQueries(queryClient),
  });

  return { create, update, replace, retry, replacePoster, remove };
}
